import { Button, Callout, Field, Input, errorMessage } from "@gf/ui";
import { MailCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { authApi } from "../../lib/api";
import { AuthHeading, AuthLayout } from "./AuthLayout";

export function ForgotPage() {
  const location = useLocation();
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter the email you sign in with.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authApi.forgot(email.trim());
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your inbox">
        <div className="gfp-auth__icon">
          <MailCheck size={22} />
        </div>
        <AuthHeading
          title="Check your inbox"
          lede={
            <>
              If <b>{email.trim()}</b> has a GramFather account, a reset link is on its way. It can take a minute, and it sometimes
              lands in spam.
            </>
          }
        />
        <Link to="/login" state={{ email }} className="gf-link">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password">
      <AuthHeading title="Forgot your password?" lede="It happens to the best of us. Enter your email and we'll send you a link to pick a new one." />
      <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            size="lg"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            placeholder="you@business.com"
          />
        </Field>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          Send reset link
        </Button>
      </form>
      <p className="gfp-auth__aside">
        Remembered it?{" "}
        <Link to="/login" state={{ email }} className="gf-link">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
